import { useEffect, useMemo, useState } from "react";
import { clockTime, longDate, monthLabel, parseYmd, shortDate, ymd } from "../lib/domain.js";

/* One person's daily check-back record, in full, since the day they joined.
 *
 * The accountability row answers "how are they doing?" in three numbers.
 * This answers "which days?", which is the question the numbers provoke and
 * the row has no space for. It is opened from the row's own figures — the
 * missed count opens it on Missed, the rate opens it on Acknowledged — so
 * the thing you clicked is the thing you are looking at.
 *
 * A modal rather than a drawer: this is a read-only drill-in with a list
 * that can run to hundreds of rows, so it wants its own scroll and no URL
 * of its own. The drawer is the playground editor's, and carries an
 * unsaved-changes guard that would mean nothing here. */
export function CheckInHistoryModal({ person, days, history, initialTab = "missed", onClose }) {
  const [tab, setTab] = useState(initialTab);
  const today = ymd(new Date());

  /* Escape closes, like every other overlay on the board. */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  /* Each day in the window sorted into one of three states. Today is not a
     miss until the day is over — the same rule the row counts by, kept in
     one place so the list and the figures can never disagree. */
  const marks = useMemo(() => {
    return days.map((d) => {
      const got = history?.[d]?.[person.email];
      if (got) return { date: d, state: "yes", at: got.at };
      if (d === today) return { date: d, state: "now" };
      return { date: d, state: "no" };
    });
  }, [days, history, person.email, today]);

  const kept = marks.filter((m) => m.state === "yes");
  const missed = marks.filter((m) => m.state === "no");
  const counted = kept.length + missed.length;
  const rate = counted ? Math.round((kept.length / counted) * 100) : null;

  const shown = tab === "missed" ? missed : tab === "kept" ? kept : marks;

  /* The list reads as a calendar, so it is broken by month rather than run
     as one long column of dates. */
  const groups = [];
  for (const m of shown) {
    const label = monthLabel(m.date);
    const last = groups[groups.length - 1];
    if (last && last.label === label) last.items.push(m);
    else groups.push({ label, items: [m] });
  }

  const name = person.name || person.email;

  return (
    <>
      <div className="modal-scrim" onClick={onClose} />
      <div className="modal wide" role="dialog" aria-modal="true" aria-label={"Check-in history for " + name}>
        <div className="modal-head">
          <span className="label">Check-in history</span>
          <button className="btn btn-sm" type="button" onClick={onClose}>
            Close
          </button>
        </div>

        <div className="modal-body">
          <div className="chhead">
            <div>
              <b className="chwho">{name}</b>
              <span className="chsince">
                {person.joinedAt
                  ? "since " + longDate(ymd(new Date(person.joinedAt)))
                  : "since the start of the record"}
                {" · "}
                {counted} {counted === 1 ? "weekday" : "weekdays"} counted
              </span>
            </div>
            <span className={"chrate" + (rate === null ? "" : rate >= 80 ? " ok" : " bad")}>
              {rate === null ? "—" : rate + "%"}
            </span>
          </div>

          {counted === 0 ? (
            <p className="empty">
              No full weekday has passed since they joined, so there is nothing to
              count yet.
            </p>
          ) : (
            <Heatmap marks={marks} />
          )}

          <div className="chtabs" role="tablist">
            {[
              ["missed", "Missed", missed.length],
              ["kept", "Acknowledged", kept.length],
              ["all", "All", marks.length],
            ].map(([key, label, n]) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={tab === key}
                className={"chtab" + (tab === key ? " on" : "")}
                onClick={() => setTab(key)}
              >
                {label} <i>{n}</i>
              </button>
            ))}
          </div>

          {!shown.length ? (
            <p className="empty">
              {tab === "missed"
                ? "Not one weekday missed since they joined."
                : tab === "kept"
                  ? "No day has been acknowledged yet."
                  : "Nothing in the record yet."}
            </p>
          ) : (
            <div className="chlist">
              {groups.map((g) => (
                <div className="chmonth" key={g.label}>
                  <div className="chmonthhead">
                    <span className="label">{g.label}</span>
                    <span className="rule" />
                  </div>
                  {g.items.map((m) => (
                    <div className={"chday " + m.state} key={m.date}>
                      <i className={"cell " + m.state} />
                      <span className="chdate">{longDate(m.date)}</span>
                      <span className="chnote">
                        {m.state === "yes"
                          ? "acknowledged " + clockTime(m.at)
                          : m.state === "now"
                            ? "today, still open"
                            : "missed"}
                      </span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

/* The whole window at a glance: a column per week, a row per weekday, which
   is the one shape that holds a year of days in the width of a paragraph.
   A long record is unreadable as a single strip of squares — that is why
   the row shows only the recent end of it and sends you here. */
function Heatmap({ marks }) {
  const byDate = new Map(marks.map((m) => [m.date, m]));
  const first = parseYmd(marks[0].date);
  if (!first) return null;

  /* Start the grid on the Monday of the first week, so every column is a
     calendar week and the rows line up as Monday through Friday. */
  const start = new Date(first);
  start.setDate(start.getDate() - ((start.getDay() + 6) % 7));
  const last = parseYmd(marks[marks.length - 1].date);

  const cells = [];
  const cursor = new Date(start);
  while (cursor <= last) {
    const day = cursor.getDay();
    if (day !== 0 && day !== 6) {
      const key = ymd(cursor);
      const mark = byDate.get(key);
      cells.push(
        mark ? (
          <i key={key} className={"cell " + mark.state} title={key + " — " + stateWord(mark)} />
        ) : (
          /* Inside the weeks the window spans, but outside the window
             itself: before they joined, or later this week. Held open so
             the columns stay aligned. */
          <i key={key} className="cell off" />
        )
      );
    }
    cursor.setDate(cursor.getDate() + 1);
  }

  return <div className="chmap">{cells}</div>;
}

function stateWord(mark) {
  if (mark.state === "yes") return "acknowledged " + clockTime(mark.at);
  if (mark.state === "now") return "today, still open";
  return "not acknowledged";
}

/* The row's start label — "since 12 Mar" — kept here so the page and the
   modal name the same day the same way. */
export const sinceStamp = (joinedAt) =>
  joinedAt ? "since " + shortDate(ymd(new Date(joinedAt))) : "whole record";
